// @ts-check
// =============================================================================
// js/data/guide-content.js - the How-To-Play reference sheet (single source).
//
// Rendered in TWO places so the rules can never drift apart:
//   - the Guide screen (screens/help.js),
//   - the in-duel Help popup (screens/duel/duel.js) for a mid-game peek.
// Copy rules, engine verification notes, and the change history live in
// screens/help.js's header and plan/help.md. Static, authored HTML - no user
// input, no escaping needed. No em dashes (owner's call).
//
// QUICKPLAY_GUIDE_HTML below is the /quickplay basics popup: the absolute
// basics only (goal, actions, card costs). Deliberately NO keywords or
// statuses, and "four actions a turn" stands in for the tick system. Every
// claim is a condensed line from GUIDE_HTML above it, so edit them TOGETHER
// when a rule changes.
// =============================================================================

/** The authored reference-sheet markup (quick reference first, rules below). */
export const GUIDE_HTML = `
<div class="guide">

	<section>
		<h2 class="guide-h">Quick Reference</h2>

		<h3 class="guide-sub">Keywords</h3>
		<dl class="guide-list">
			<dt>Breeding</dt><dd>At the start of your turn, every Fluffle you
				control grows: half its Health is added to both its Health and its
				Meat value.</dd>
			<dt>Ambusher</dt><dd>Takes no counter-damage when it attacks.</dd>
			<dt>Flanker</dt><dd>Can attack into the two neighboring lanes, not
				just its own.</dd>
			<dt>Flier</dt><dd>Flies over blockers: can attack the back row or the
				enemy leader even when the lane is guarded.</dd>
			<dt>Foolish</dt><dd>Never strikes back when attacked.</dd>
			<dt>Lazy</dt><dd>Can't attack on the turn it is played.</dd>
			<dt>Ravager</dt><dd>When its attack defeats a creature, it devours it
				on the spot.</dd>
		</dl>

		<h3 class="guide-sub">Statuses</h3>
		<dl class="guide-list">
			<dt>Stunned</dt><dd>Not ready to attack. Cards may say distracted,
				asleep, or otherwise indisposed. It's all the same stun.</dd>
		</dl>

		<h3 class="guide-sub">Your Actions</h3>
		<dl class="guide-list">
			<dt>Draw</dt><dd>Take a card from your deck.</dd>
			<dt>Harvest</dt><dd>Gain 1 Crop.</dd>
			<dt>Devour</dt><dd>Eat a creature you own or that has zero health.
				Gain Meat. A player can be devoured if their deck is gone.</dd>
			<dt>Play</dt><dd>Play a card from your hand, possibly paying a meat
				or crop cost.</dd>
			<dt>Attack</dt><dd>Fight a creature in the same lane, or remove
				cards off the opponent's deck.</dd>
		</dl>

		<h3 class="guide-sub">Controls</h3>
		<p>Tap a card or creature, then tap a glowing target. Press-and-hold
		any card, creature, egg, or leader to read it up close.</p>
	</section>

	<section>
		<h2 class="guide-h">Game Start</h2>
		<dl class="guide-list">
			<dt>Starting Resources</dt><dd>Each player begins with 5 Crop and a
				hand of 5 cards.</dd>
			<dt>Mulligan</dt><dd>Redraw your starting hand before taking your
				first turn.</dd>
			<dt>Going First</dt><dd>On the very first turn of the game, players
				can't be attacked or targeted by their opponent.</dd>
		</dl>
	</section>

	<section>
		<h2 class="guide-h">Taking Your Turn</h2>
		<dl class="guide-list">
			<dt>Ticks</dt><dd>You start each turn with four ticks. Each
				action costs a tick.</dd>
			<dt>Winning</dt><dd>Win the game by removing all cards from your
				opponent's deck and then devouring them!</dd>
		</dl>
	</section>

	<section>
		<h2 class="guide-h">Card Types</h2>
		<dl class="guide-list">
			<dt class="guide-type--free">Free Cards</dt><dd>Blue cards are
				free. They still cost an action to play.</dd>
			<dt class="guide-type--crop">Crop Cards</dt><dd>Green cards cost
				crop. Crop can always be gained by harvesting.</dd>
			<dt class="guide-type--meat">Meat Cards</dt><dd>Red cards cost
				meat. Meat can be gained by devouring creatures.</dd>
			<dt class="guide-type--egg">Egg Cards</dt><dd>Yellow cards are free
				to play. They take time to hatch.</dd>
		</dl>
	</section>

</div>`;

/**
 * The /quickplay basics sheet: what a brand-new player must know to take a
 * first turn, and nothing else. Shown once, over the auto-started bot duel
 * (app/quickplay.js). Same .guide markup so help.css styles both. The modal's
 * own "How To Play" title is the only header before the actions; the win
 * condition deliberately sits LAST, as the parting thought.
 */
export const QUICKPLAY_GUIDE_HTML = `
<div class="guide">

	<section>
		<p>You get four actions each turn:</p>
		<dl class="guide-list">
			<dt>Draw</dt><dd>Take a card from your deck.</dd>
			<dt>Harvest</dt><dd>Gain 1 Crop.</dd>
			<dt>Devour</dt><dd>Eat a creature you own, or one at zero health.
				Gain Meat.</dd>
			<dt>Play</dt><dd>Put a card from your hand on the board, paying its
				cost.</dd>
			<dt>Attack</dt><dd>Fight the creature in the same lane, or bite the
				enemy deck if the lane is open.</dd>
		</dl>
	</section>

	<section>
		<h2 class="guide-h">Card Costs</h2>
		<dl class="guide-list">
			<dt class="guide-type--free">Blue</dt><dd>Free to play.</dd>
			<dt class="guide-type--crop">Green</dt><dd>Costs Crop.</dd>
			<dt class="guide-type--meat">Red</dt><dd>Costs Meat.</dd>
			<dt class="guide-type--egg">Yellow</dt><dd>Takes time to
				hatch.</dd>
		</dl>
	</section>

	<section>
		<h2 class="guide-h">How To Win</h2>
		<p>Attack your opponent to bite cards off their deck. When their deck
		is empty, Devour them to win the game!</p>
	</section>

</div>`;
